import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatCheckboxField } from "blocks-nextjs/blocks-react/components/CheckboxField/CheckboxField";
import { CatDrawer } from "blocks-nextjs/blocks-react/components/Drawer";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconFilter } from "blocks-nextjs/blocks-react/components/Icons/Filter/Filter";
import { CatSearchForm } from "blocks-nextjs/blocks-react/components/SearchForm/SearchForm";
import React, { useMemo, useState } from "react";
import { useGetAssetsV1Query } from "../../services/query/assets-v1.query";
import { useCountAssetsQuery, useSearchAssetsQuery } from "../../services/query/assets-v2.query";
import useGlobalStore from "../../store/useGlobalStore";
import { AssetPropertiesForSorting } from "../../swagger/assets-v2/models/AssetPropertiesForSorting";
import { FilterType } from "../../swagger/assets-v2/models/FilterType";
import { AssetType } from "./AddAssetsListCard";
const MIN_SEARCH_LENGTH = 3;
const LIST_MESSAGE_CLASS = "flex flex-col items-center justify-center h-full text-center text-sm text-gray-500";
const AddAssetDrawer = ({ isActive, isNonCatAsset = false, onAddAssets, onClose, onFilterClick, preSelectedAssets }) => {
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedAssets, setSelectedAssets] = useState(() => isActive ? (preSelectedAssets ?? []) : []);
    const [wasActive, setWasActive] = useState(isActive);
    // Re-seed the selection with the project's current assets each time the drawer opens.
    if (isActive !== wasActive) {
        setWasActive(isActive);
        setSelectedAssets(isActive ? (preSelectedAssets ?? []) : []);
        setSearchTerm("");
    }
    const isSearchApplied = searchTerm.trim().length >= MIN_SEARCH_LENGTH;
    const requestBody = isSearchApplied
        ? {
            filters: [
                {
                    isCaseSensitive: false,
                    propertyName: "serialNumber",
                    type: FilterType.CONTAINS,
                    value: searchTerm.trim()
                }
            ],
            logicalExpression: "$0"
        }
        : undefined;
    // Every asset request must be party-scoped: CAT sessions down-select, everyone else is bound to their own party.
    const partyScopeParams = useMemo(() => {
        if (!userPartyNumber) {
            return {};
        }
        return primaryPartyType === "CAT"
            ? { downSelectPartyNumber: userPartyNumber }
            : { partyNumber: userPartyNumber };
    }, [primaryPartyType, userPartyNumber]);
    const { data: assetsResponse, isError: isSearchError, isFetching: isSearchFetching, isPending: isSearchPending, refetch: refetchSearchAssets } = useSearchAssetsQuery({
        enabled: isActive && !isNonCatAsset && isSearchApplied,
        paramsList: {
            ...partyScopeParams,
            orderBy: ["ASC"],
            sortBy: [AssetPropertiesForSorting.SERIAL_NUMBER]
        },
        requestBody
    });
    const { data: countAssetsData, isError: isCountError, refetch: refetchCountAssets } = useCountAssetsQuery({
        enabled: isActive && !isNonCatAsset && isSearchApplied,
        paramsList: partyScopeParams,
        requestBody
    });
    const { data: nonCatAssetsResponse, isError: isNonCatError, isFetching: isNonCatFetching, isPending: isNonCatPending, refetch: refetchNonCatAssets } = useGetAssetsV1Query({
        enabled: isActive && isNonCatAsset,
        paramsList: {
            ...partyScopeParams,
            ...(isSearchApplied && { serialNumber: searchTerm.trim() })
        }
    });
    const isAssetsLoading = isNonCatAsset
        ? isNonCatPending && isNonCatFetching
        : isSearchApplied && isSearchPending && isSearchFetching;
    const isAssetsError = isNonCatAsset ? isNonCatError : isSearchError || isCountError;
    const retryAssets = () => {
        if (isNonCatAsset) {
            refetchNonCatAssets();
            return;
        }
        refetchSearchAssets();
        refetchCountAssets();
    };
    const searchResults = useMemo(() => {
        const assets = isNonCatAsset ? (nonCatAssetsResponse?.assets ?? []) : (assetsResponse?.assets ?? []);
        return assets.map((asset) => ({
            assetMetadata: {
                model: asset.metadata?.model,
                modelYear: asset.metadata?.modelYear,
                productFamily: {
                    code: asset.metadata?.productFamily?.code,
                    iconUrl: asset.metadata?.icon?.imageUrl,
                    name: asset.metadata?.productFamily?.name
                }
            },
            assetName: asset.metadata?.name ?? "",
            assetSerialNumber: asset.metadata?.serialNumber ?? "",
            assetSourceId: asset.id,
            assetSourceType: AssetType.MACHINE
        }));
    }, [assetsResponse, isNonCatAsset, nonCatAssetsResponse]);
    const isSelected = (asset) => selectedAssets.some((selected) => selected.assetSourceId === asset.assetSourceId);
    // v1 has no count endpoint or total field, so the Non-Cat tally is limited to the fetched page.
    const availableAssetsCount = isNonCatAsset ? searchResults.length : (countAssetsData?.count ?? 0);
    const unselectedAssets = searchResults.filter((asset) => !isSelected(asset));
    const toggleAsset = (asset, checked) => {
        setSelectedAssets(checked
            ? [...selectedAssets, asset]
            : selectedAssets.filter((selected) => selected.assetSourceId !== asset.assetSourceId));
    };
    const handleClose = () => {
        setSelectedAssets([]);
        setSearchTerm("");
        onClose();
    };
    const renderAssetRow = (asset, index, selected) => (_jsxs("li", { className: `flex items-center gap-[16px] px-[16px] py-[8px] min-h-[64px] rounded bg-white border ${selected ? "border-black" : "border-[#CCCCCC]"}`, "data-test": selected ? `selected-asset-${index}` : `asset-${index}`, children: [asset.assetMetadata?.productFamily?.iconUrl && (_jsx("figure", { "aria-hidden": "true", children: _jsx("img", { alt: "Asset", className: "w-[56px] h-[40px] object-contain", "data-test": `asset-image-${asset.assetSourceId}`, src: asset.assetMetadata.productFamily.iconUrl }) })), _jsxs("div", { className: "grow min-w-0", children: [_jsx("p", { className: "font-bold text-base leading-6", "data-test": `asset-name-${asset.assetSourceId}`, children: asset.assetName }), _jsx("p", { className: "font-normal text-sm leading-5", "data-test": `asset-serial-${asset.assetSourceId}`, children: [asset.assetSerialNumber, asset.assetMetadata?.model].filter(Boolean).join(" - ") })] }), _jsx(CatCheckboxField, { checked: selected, "data-test": `asset-checkbox-${asset.assetSourceId}`, onBlCheckboxChange: (e) => toggleAsset(asset, e.detail.checked) })] }, asset.assetSourceId));
    const renderAssetListBody = () => {
        if (isAssetsLoading) {
            return (_jsx("li", { className: LIST_MESSAGE_CLASS, "data-test": "assets-loading", children: _jsx("p", { children: "Loading assets..." }) }));
        }
        if (isAssetsError) {
            return (_jsxs("li", { className: LIST_MESSAGE_CLASS, "data-test": "assets-error", children: [_jsx("p", { children: "Error: Data failed to load." }), _jsx("p", { children: "Please try again later." }), _jsx(CatButton, { className: "mt-[8px]", "data-test": "assets-retry-button", onClick: retryAssets, size: "sm", variant: "ghost", children: "Retry" })] }));
        }
        if (isSearchApplied && unselectedAssets.length === 0) {
            return (_jsxs("li", { className: LIST_MESSAGE_CLASS, "data-test": "no-matching-assets", children: [_jsx("p", { children: "No matching assets or schedules found." }), _jsx("p", { children: "Try adjusting your filters or search terms." })] }));
        }
        return unselectedAssets.map((asset, index) => renderAssetRow(asset, index, false));
    };
    return (_jsx(CatDrawer, { "data-test": "add-asset-drawer", disableBackdropClick: false, hasBackdrop: true, isActive: isActive, onBlDrawerClose: handleClose, width: "33%", children: _jsxs(_Fragment, { children: [_jsx("div", { "data-test": "add-asset-drawer-header", slot: "header", children: _jsx(CatHeading, { "data-test": "add-asset-drawer-title", level: "h1", variant: "title-sm", children: "Add Asset" }) }), _jsxs("div", { className: "flex flex-col gap-[16px]", children: [_jsxs("div", { className: "flex items-center gap-[16px]", children: [_jsx("div", { className: "grow", children: _jsx(CatSearchForm, { arialListLabel: "Search Serial Number", "data-test": "add-asset-search-serial-number", isEmpty: true, label: "Search Serial Number", onBlChange: (e) => setSearchTerm(e.detail.value ?? ""), placeholder: "Search Serial Number", size: "lg" }, isActive ? "open" : "closed") }), onFilterClick && (_jsx(CatButton, { "data-test": "add-asset-filter-button", hideText: true, onClick: onFilterClick, size: "lg", children: _jsxs(React.Fragment, { children: ["Filter", _jsx(CatIconFilter, { slot: "before" })] }, ".0") }))] }), _jsxs("div", { className: "flex flex-col gap-[16px] p-[16px] bg-[#F2F2F2]", children: [selectedAssets.length > 0 && (_jsxs("section", { className: "flex flex-col gap-[8px]", children: [_jsxs("p", { className: "text-right text-sm leading-5", "data-test": "selected-assets-count", children: [_jsx("span", { className: "font-bold", children: selectedAssets.length }), " Selected"] }), _jsx("ul", { className: "flex flex-col gap-[8px]", children: selectedAssets.map((asset, index) => renderAssetRow(asset, index, true)) })] })), _jsxs("section", { className: "flex flex-col gap-[8px]", children: [_jsxs("p", { className: "text-right text-sm leading-5", "data-test": "available-assets-count", children: [!isAssetsLoading && !isAssetsError && (_jsx("span", { className: "font-bold", children: availableAssetsCount })), " ", "Assets"] }), _jsx("ul", { className: "flex flex-col gap-[8px] max-h-[520px] overflow-y-auto", children: renderAssetListBody() })] })] })] }), _jsxs("div", { className: "flex items-center gap-[8px]", "data-test": "add-asset-drawer-footer", slot: "footer", children: [_jsx(CatButton, { "data-test": "add-assets-button", disabled: selectedAssets.length === 0, onClick: () => {
                                onAddAssets(selectedAssets);
                                handleClose();
                            }, size: "md", variant: "primary", children: `Add ${selectedAssets.length} Assets` }), _jsx(CatButton, { "data-test": "add-asset-cancel-button", onClick: handleClose, size: "md", variant: "ghost", children: "Cancel" })] })] }) }));
};
export default AddAssetDrawer;
